/**
 * Approvals inbox — the unified "changes I can decide" feed, shared by the
 * /approvals page (every request) and the Knowledge Base Approvals tab
 * (document requests only).
 *
 * ONE backend feed: GET /access-requests?decidable_by=me. A person asking to
 * be let in and an author asking to widen a document's audience are the same
 * fact — a proposed change to the access graph awaiting a decision — so they
 * are one collection, and each row names its decider tier in `authority`.
 * There is no merge, no second query key and no second poll loop.
 *
 * The feed is POLLED — requests are opened by OTHER users, so no local
 * mutation invalidates this cache. Decisions are server-authoritative: call the
 * API, then invalidate the inbox AND the documents list (an approved expansion
 * changes a row's visibility chips).
 */

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Button } from '@/components/ui/button';
import {
  ALREADY_DECIDED_CODE,
  decideAccessRequest,
  isQuorumPending,
  listAccessRequestInbox,
  type AccessRequest,
  type AccessRequestListItem,
} from '@/api/endpoints/accessRequests';
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import { accessKeys } from '@/hooks/useAccess';
import { ApiError } from '@/api/client';
import { createLogger } from '@/utils/logger';
import { POLL_INTERVAL_PUBLISH_REQUESTS } from '@/lib/constants';
import { formatDayStamp } from '@/utils/dates';
import { ACCESS_INBOX_QUERY_KEY, DOCUMENTS_QUERY_KEY } from '@/lib/queryKeys';
import { toast } from '@/hooks/use-toast';
import { errorToast } from '@/hooks/errorToast';

const logger = createLogger('ApprovalsInbox');

/** A request that concerns a document (vs a unit/node). */
export const isDocumentAccessRequest = (req: AccessRequest): boolean =>
  req.resource_type === 'document';

/** The grant level in words. Absent renders as absent, never as a default. */
const levelLabel = (req: AccessRequest, t: TFunction): string | undefined =>
  req.access_level
    ? t(`documents.sharing.role.${req.access_level}`, { defaultValue: req.access_level })
    : undefined;

/**
 * What the request asks for, in words. THREE shapes, and the shape is read off
 * the row (`change` + `grantee_type`), never guessed from the tier.
 *
 * It sits outside the component, taking `t`, so each arm can be asserted
 * without rendering a page and a poll loop.
 */
export const describeRequest = (req: AccessRequest, t: TFunction): string => {
  const title = req.resource_title || req.resource_id;
  if (req.change === 'visibility') {
    return t('documents.approvals.label.publish', {
      title,
      defaultValue: 'Publish {{title}} to the organisation',
    });
  }
  if (req.grantee_type === 'user') {
    return t('documents.approvals.label.grantToUser', {
      name: req.grantee_name || req.grantee_id || req.requester_name || req.requested_by,
      level: levelLabel(req, t),
      defaultValue: '{{name}} wants {{level}} access',
    });
  }
  return t('documents.approvals.label.grantToGroup', {
    title,
    grantee: req.grantee_name || req.grantee_id,
    defaultValue: 'Share {{title}} with {{grantee}}',
  });
};

export interface ApprovalsInboxData {
  requests: AccessRequestListItem[];
  /** True when the page carried `Link: rel="next"` — more pending items exist. */
  hasMore: boolean;
  /** Pending decisions in the feed. */
  count: number;
  isPending: boolean;
  isError: boolean;
  isFetching: boolean;
  refetch: () => void;
}

/**
 * The inbox feed as one hook. The query key is shared module-wide, so every
 * consumer (Home badge, /approvals page, Knowledge Base tab) reads the same
 * cache entry — one poll loop, not one per screen.
 */
export function useApprovalsInbox(): ApprovalsInboxData {
  const query = useAuthenticatedQuery<{ requests: AccessRequestListItem[]; next: string | null }>({
    queryKey: ACCESS_INBOX_QUERY_KEY,
    queryFn: () => listAccessRequestInbox(),
    refetchInterval: POLL_INTERVAL_PUBLISH_REQUESTS,
  });

  const requests = query.data?.requests ?? [];
  return {
    requests,
    hasMore: query.data !== undefined && query.data.next !== null,
    count: requests.length,
    isPending: query.isPending,
    isError: query.isError,
    isFetching: query.isFetching,
    refetch: () => {
      void query.refetch();
    },
  };
}

interface ApprovalRequestListProps {
  requests: AccessRequestListItem[];
}

/**
 * The decision list: one row per pending request with Approve plus a refusal.
 * The CALLER owns the surrounding 4 states (loading / error / empty / data) —
 * this component only renders rows.
 *
 * The row's WORDS are keyed on `authority`, not decoration: the wire carries
 * one terminal status ('denied') for both tiers, and the UI says which refusal
 * it was. An author whose guideline was refused must read "rejected"; a nurse
 * refused access must read "denied".
 */
export function ApprovalRequestList({ requests }: ApprovalRequestListProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Decision in flight (disables every row's buttons; spinner on the one row).
  const [decidingId, setDecidingId] = useState<string | null>(null);

  // Refresh everything a decision can change: the inbox, the team boards'
  // own read of the same queue, and the document list (an approved expansion
  // changes a row's visibility chips).
  const refreshAfterDecision = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ACCESS_INBOX_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: accessKeys.all }),
      queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY }),
    ]);

  /** Who opened the change, in whatever the server resolved. */
  const requesterLabel = (req: AccessRequest): string | undefined =>
    req.requester_name;

  const requestLabel = (req: AccessRequest): string => describeRequest(req, t);

  /** "Reject" for a clinical approver, "Deny" for a resource owner. */
  const refuseLabel = (req: AccessRequest): string =>
    req.authority === 'approver'
      ? t('documents.approvals.reject', 'Reject')
      : t('documents.approvals.deny', 'Deny');

  /** The same distinction in the outcome toast. */
  const decidedMessage = (req: AccessRequest, decision: 'approve' | 'deny'): string => {
    if (req.authority === 'approver') {
      return decision === 'approve'
        ? t('documents.approvals.approved', 'Approved — the change is now live.')
        : t('documents.approvals.rejected', 'Request rejected.');
    }
    return decision === 'approve'
      ? t('documents.approvals.accessGranted', 'Access granted.')
      : t('documents.approvals.accessDenied', 'Request denied.');
  };

  // Server-authoritative decision. A 202 quorum-pending response means this
  // sign-off was recorded but more approvals are needed — the request stays
  // in the inbox.
  //
  // The branch is on the RESPONSE SHAPE, never on the row's tier. Only the
  // approver tier has a quorum today, so only it can answer quorum-pending —
  // but a tier that later grows one needs no change here. And the quorum
  // figure has to come from the response: it is per-category server config, so
  // `signoffs.length` alone would tell an approver "1 signature" with no way to
  // learn whether that finished it.
  const handleDecide = async (req: AccessRequest, decision: 'approve' | 'deny') => {
    setDecidingId(req.id);
    try {
      const result = await decideAccessRequest(req.id, decision);
      if (isQuorumPending(result)) {
        toast({
          description: t('documents.approvals.quorumPending', {
            approvals: result.approvals,
            quorum: result.quorum,
            defaultValue: 'Approval recorded — {{approvals}} of {{quorum}} required',
          }),
        });
      } else {
        toast({ description: decidedMessage(req, decision) });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Decision failed';
      logger.error('Request decision failed', { requestId: req.id, decision, error: msg });
      const alreadyDecided = err instanceof ApiError && err.code === ALREADY_DECIDED_CODE;
      errorToast({
        context: 'accessRequestDecision',
        description: alreadyDecided
          ? t('documents.approvals.alreadyDecided', 'This request was already decided.')
          : t('documents.approvals.decideError', 'Could not record the decision. Please try again.'),
        // No retry on ALREADY_DECIDED — re-firing can't change a settled
        // decision; the refresh in `finally` re-reads the committed outcome.
        onRetry: alreadyDecided ? undefined : () => void handleDecide(req, decision),
      });
    } finally {
      setDecidingId(null);
      // Refresh on EVERY outcome: success drops the row; ALREADY_DECIDED
      // re-reads the other decider's committed outcome; a transient failure
      // re-read is a no-op for a row that truly didn't change — and repairs
      // the view when the decision landed server-side but the response was
      // lost.
      await refreshAfterDecision();
    }
  };

  const spinner = (
    <div
      className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"
      role="status"
      aria-label={t('common.loading')}
    />
  );

  return (
    <ul className="divide-y divide-neutral-100">
      {requests.map((req) => (
        <li key={req.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-neutral-900 truncate">
              {req.resource_title || req.resource_id}
            </p>
            <p className="text-xs text-neutral-700 mt-0.5">{requestLabel(req)}</p>
            {/* Same wall-visible-screen guard as the requester email below:
                free-text justification is clamped to one bounded line (it can
                carry whatever the requester typed); the full text stays
                available via the title tooltip on pointer devices. */}
            {req.justification && (
              <p
                className="text-xs text-neutral-500 mt-0.5 italic truncate max-w-[20rem]"
                title={req.justification}
              >
                {req.justification}
              </p>
            )}
            {/* Who opened it, then when. The server resolves the display
                name; where only an email arrives, the username portion is
                shown — the inbox can be on a wall-visible shared screen, so
                the full address stays in the title tooltip. Neither present
                leaves the date standing alone. */}
            <p className="text-xs text-neutral-500 mt-0.5">
              {requesterLabel(req)}
              {requesterLabel(req) ? ' · ' : ''}
              {formatDayStamp(req.created_at, t)}
            </p>
            {/* The quorum trail: a decider reading a queue needs to tell a
                change one signature short from one nobody has touched. Only
                the approver tier has a ledger, and a count of zero is the
                same fact as the row simply being pending. */}
            {req.authority === 'approver' && req.signoffs.length > 0 && (
              <p className="text-xs text-neutral-500 mt-0.5">
                {t('documents.approvals.signoffsRecorded', {
                  count: req.signoffs.length,
                  defaultValue: '{{count}} approval(s) recorded so far',
                })}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {decidingId === req.id && spinner}
            <Button
              size="sm"
              onClick={() => void handleDecide(req, 'approve')}
              disabled={decidingId !== null}
              className="min-h-[44px]"
            >
              {t('documents.approvals.approve', 'Approve')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void handleDecide(req, 'deny')}
              disabled={decidingId !== null}
              className="text-red-600 border-red-200 hover:bg-red-50 min-h-[44px]"
            >
              {refuseLabel(req)}
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
}
